import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create Project
export const createProject = createAsyncThunk(
  'projects/createProject',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}dashboard/create-project`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get All Projects
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async ({ page, limit, search = '', deleted = '', homepage = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}dashboard/get-all-dashboard-projects?page=${page}&limit=${limit}&search=${search}&deleted=${deleted}&homepage=${homepage}`
      );
      return {
        items: response.data.items,
        total: response.data.totalItems,
        currentPage: response.data.page,
        pageSize: limit,
        totalPages: Math.ceil(response.data.totalItems / limit),
        itemCount: response.data.items.length
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete Project
export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}dashboard/delete-project/${projectId}`
      );
      return projectId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Project
export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ projectId, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/update-project/${projectId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const setHomepageOrder = createAsyncThunk(
  'projects/setHomepageOrder',
  async ({ projectId, order }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}dashboard/set-project-homepage-order/${projectId}`,
        { order },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        projectId,
        order,
        message: response.data.message,
        replacedProject: response.data.replacedProject || null
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to update position',
        status: error.response?.status
      });
    }
  }
);

const projectSlice = createSlice({
  name: 'projects',
  initialState: {
    projects: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    itemCount: 0,
    success: false
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create Project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.projects.unshift({ ...action.payload, status: 'active' });
        state.total += 1;
        state.totalPages = Math.ceil(state.total / state.pageSize);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.items;
        state.total = action.payload.total;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
        state.totalPages = action.payload.totalPages;
        state.itemCount = action.payload.itemCount;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Project
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.projects = state.projects.map(project =>
          project.id === action.payload
            ? { ...project, status: 'deleted', homepage_order: 0 }
            : project
        );
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Project
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const index = state.projects.findIndex(p => p.id === action.payload.projectId);
        if (index !== -1) {
          state.projects[index] = { ...state.projects[index], ...action.payload, status: 'active' };
        }
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Set Homepage Order
      .addCase(setHomepageOrder.fulfilled, (state, action) => {
        const { projectId, order } = action.payload;
        state.projects = state.projects.map(project =>
          project.id === projectId
            ? { ...project, homepage_order: order }
            : project
        );
        if (action.payload.replacedProject) {
          state.projects = state.projects.map(project =>
            project.id === action.payload.replacedProject.id
              ? { ...project, homepage_order: 0 }
              : project
          );
        }
      })
      .addCase(setHomepageOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess } = projectSlice.actions;
export default projectSlice.reducer;